// src/pages/ClinicQueuePage.jsx — "Today's clinic" (route /clinic/queue)
//
// Front-desk board for navigators / admin / support staff: Expected → Waiting →
// In consultation → Done. Check in expected patients, record vitals for those
// waiting, add walk-ins. Refreshes every 30s while the tab is visible.
// Contract: docs/visit-workflow-contract.md ("Queue — BE-4").

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import Icon from '../components/Icon';
import VitalsIntakeForm from '../components/VitalsIntakeForm';
import PatientSearchPicker from '../components/chat/PatientSearchPicker';
import useDialogFocus from '../hooks/useDialogFocus';
import { useToast } from '../context/ToastContext';
import { clinicQueueAPI } from '../services/clinicQueueApi';
import { doctorAPI } from '../services/api';
import {
  COLUMNS, bucketQueue, summaryStats, waitMinutes, isLongWait, formatWait, formatClock,
  isWalkIn, todayIso,
} from '../utils/clinicQueue';

const REFRESH_MS = 30000;
const BREADCRUMBS = [{ label: "Today's clinic" }];

const STATUS_BADGE = {
  SCHEDULED: ['badge-confirmed', 'Scheduled'],
  CONFIRMED: ['badge-confirmed', 'Confirmed'],
  ARRIVED: ['badge-pending', 'Arrived'],
  IN_CONSULTATION: ['badge-purple', 'In consultation'],
  COMPLETED: ['badge-active', 'Completed'],
  NO_SHOW: ['badge-inactive', 'No show'],
  CANCELLED: ['badge-inactive', 'Cancelled'],
};

const apiError = (e, fallback) => {
  const d = e?.response?.data;
  return (typeof d === 'string' && d) || d?.message || d?.error || fallback;
};
const doctorLabel = (d) => `Dr. ${[d.firstName, d.lastName].filter(Boolean).join(' ') || d.name || ''}`.trim();

function QueueCard({ entry, now, busy, onCheckIn, onVitals, onStart }) {
  const status = String(entry.status || '').toUpperCase();
  const long = isLongWait(entry, now);
  const wait = waitMinutes(entry, now);
  const [badgeCls, badgeLabel] = STATUS_BADGE[status] || ['badge-unknown', status || 'Unknown'];
  const closed = status === 'NO_SHOW' || status === 'CANCELLED';
  return (
    <li className={`queue-card${long ? ' queue-card--long-wait' : ''}${closed ? ' queue-card--closed' : ''}`}
      data-testid="queue-card" data-status={status}>
      <div className="queue-card-top">
        <span className="queue-token">{entry.token || '—'}</span>
        <span>
          {isWalkIn(entry) && <span className="badge badge-purple" style={{ marginRight: 6 }}>Walk-in</span>}
          {(closed || status === 'COMPLETED') && <span className={`badge ${badgeCls}`}>{badgeLabel}</span>}
        </span>
      </div>
      <div className="queue-card-name">
        {entry.patientId ? <Link to={`/patients/${entry.patientId}`}>{entry.patientName || 'Unnamed patient'}</Link> : (entry.patientName || 'Unnamed patient')}
      </div>
      <div className="queue-card-meta">
        {entry.doctorName || 'No doctor'}
        {status === 'SCHEDULED' || status === 'CONFIRMED' ? (entry.scheduledTime ? ` · ${formatClock(entry.scheduledTime)}` : '') : ''}
        {entry.reason ? ` · ${entry.reason}` : ''}
      </div>
      {(status === 'ARRIVED' || status === 'IN_CONSULTATION' || status === 'COMPLETED') && (
        <div className={`queue-card-wait${long ? ' queue-card-wait--long' : ''}`}>
          {status === 'ARRIVED' ? 'Waiting ' : 'Waited '}{formatWait(wait)}{long ? ' — over 20 min' : ''}
        </div>
      )}
      {status === 'ARRIVED' && entry.vitalsRecorded && <div><span className="badge badge-active">Vitals recorded</span></div>}
      {(status === 'SCHEDULED' || status === 'CONFIRMED') && (
        <div className="queue-card-actions">
          <button type="button" className="btn btn-primary btn-sm" disabled={busy}
            onClick={() => onCheckIn(entry)} aria-label={`Check in ${entry.patientName || 'patient'}`}>
            {busy ? 'Checking in…' : 'Check in'}
          </button>
        </div>
      )}
      {status === 'ARRIVED' && (
        <div className="queue-card-actions">
          <button type="button" className="btn btn-secondary btn-sm"
            onClick={() => onVitals(entry)}
            aria-label={`${entry.vitalsRecorded ? 'Record again, vitals' : 'Record vitals'} for ${entry.patientName || 'patient'}`}>
            {entry.vitalsRecorded ? 'Record again' : 'Record vitals'}
          </button>
          <button type="button" className="btn btn-primary btn-sm" disabled={busy}
            onClick={() => onStart(entry)} aria-label={`Start consultation for ${entry.patientName || 'patient'}`}>
            {busy ? 'Starting…' : 'Start consultation'}
          </button>
        </div>
      )}
    </li>
  );
}

function WalkInDialog({ patient, doctors, onClose, onCreated }) {
  const { toast } = useToast();
  const [doctorId, setDoctorId] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const selectRef = useRef(null);
  const dialogRef = useRef(null);
  useDialogFocus(dialogRef);
  useEffect(() => { selectRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  async function submit(e) {
    e.preventDefault();
    if (!doctorId) { setError('Choose a doctor.'); return; }
    setSaving(true); setError('');
    try {
      const r = await clinicQueueAPI.walkIn({ patientId: patient.id, doctorId, reason: reason.trim() || undefined });
      toast.success(`${patient.name} added to the queue${r.data?.token ? ` as ${r.data.token}` : ''}`);
      onCreated();
    } catch (err) {
      setError(apiError(err, 'Could not add the walk-in. Please try again.'));
      setSaving(false);
    }
  }

  return (
    <div className="dialog-backdrop" onClick={saving ? undefined : onClose}>
      <form className="dialog" role="dialog" aria-modal="true" aria-labelledby="walkin-title" ref={dialogRef}
        onClick={(e) => e.stopPropagation()} onSubmit={submit} noValidate>
        <div className="dialog-title" id="walkin-title">Walk-in</div>
        <div className="dialog-sub">{patient.name} will be checked in straight away.</div>
        <div className="form-group">
          <label className="form-label" htmlFor="walkin-doctor">Doctor <span className="required">*</span></label>
          <select id="walkin-doctor" ref={selectRef} className="form-select" value={doctorId}
            onChange={(e) => setDoctorId(e.target.value)}>
            <option value="">Select a doctor…</option>
            {doctors.map((d) => <option key={d.id} value={d.id}>{doctorLabel(d)}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="walkin-reason">Reason for visit</label>
          <input id="walkin-reason" className="form-input" type="text" maxLength={200} value={reason}
            onChange={(e) => setReason(e.target.value)} placeholder="e.g. Fever since two days" />
        </div>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <div className="dialog-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Adding…' : 'Add to queue'}</button>
        </div>
      </form>
    </div>
  );
}

export default function ClinicQueuePage() {
  const { toast } = useToast();
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState('');
  const [date, setDate] = useState(() => todayIso());
  const [busyId, setBusyId] = useState(null);
  const [vitalsFor, setVitalsFor] = useState(null);
  const [pickingPatient, setPickingPatient] = useState(false);
  const [walkInPatient, setWalkInPatient] = useState(null);
  const reqRef = useRef(0);

  useEffect(() => {
    doctorAPI.list()
      .then((r) => setDoctors(Array.isArray(r.data) ? r.data : (r.data?.content || [])))
      .catch(() => setDoctors([]));
  }, []);

  // `silent` refreshes keep the board on screen: a failed background refresh
  // only shows the error when there is nothing to show yet.
  const load = useCallback(async (silent = false) => {
    const req = ++reqRef.current;
    if (!silent) setRefreshing(true);
    try {
      const r = await clinicQueueAPI.list({ date, doctorId });
      if (req !== reqRef.current) return;
      setEntries(Array.isArray(r.data) ? r.data : []);
      setStatus('ready');
      setNow(new Date());
    } catch (e) {
      if (req !== reqRef.current) return;
      if (!silent || status !== 'ready') {
        setStatus('error');
        setErrorMsg(apiError(e, 'Could not load the queue.'));
      }
    } finally {
      if (req === reqRef.current) setRefreshing(false);
    }
  }, [date, doctorId, status]);

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => { setStatus('loading'); load(false); }, [date, doctorId]); // eslint-disable-line

  // Auto-refresh every 30s, paused while the tab is hidden; catch up on return.
  useEffect(() => {
    const tick = () => { if (!document.hidden) loadRef.current(true); };
    const id = setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', tick); };
  }, []);

  const buckets = useMemo(() => bucketQueue(entries), [entries]);
  const stats = useMemo(() => summaryStats(entries, now), [entries, now]);

  async function handleCheckIn(entry) {
    setBusyId(entry.appointmentId);
    try {
      const r = await clinicQueueAPI.checkIn(entry.appointmentId);
      toast.success(`${entry.patientName || 'Patient'} checked in${r.data?.token ? ` — token ${r.data.token}` : ''}`);
    } catch (e) {
      toast.error(e?.response?.status === 409
        ? 'This patient is already checked in or the visit is closed. Refreshing the queue.'
        : apiError(e, 'Could not check in. Please try again.'));
    } finally {
      setBusyId(null);
      load(true);
    }
  }

  async function handleStart(entry) {
    setBusyId(entry.appointmentId);
    try {
      await clinicQueueAPI.startConsultation(entry.appointmentId);
      toast.success(`${entry.patientName || 'Patient'} is now in consultation`);
    } catch (e) {
      toast.error(e?.response?.status === 409
        ? 'This patient is no longer waiting. Refreshing the queue.'
        : apiError(e, 'Could not start the consultation. Please try again.'));
    } finally {
      setBusyId(null);
      load(true);
    }
  }

  const isToday = date === todayIso();
  const unknownCount = buckets.other.length;

  return (
    <AppShell breadcrumbs={BREADCRUMBS}>
      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">Today's clinic</div>
          <div className="page-subtitle">Check patients in, record vitals, start consultations and add walk-ins</div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-primary" onClick={() => setPickingPatient(true)}>
            <Icon name="plus" size={16} /> Walk-in
          </button>
        </div>
      </div>

      <div className="toolbar fade-in-up delay-2" style={{ flexWrap: 'wrap' }}>
        <div>
          <label className="form-label" htmlFor="queue-doctor">Doctor</label>
          <select id="queue-doctor" className="form-select" value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
            <option value="">All doctors</option>
            {doctors.map((d) => <option key={d.id} value={d.id}>{doctorLabel(d)}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label" htmlFor="queue-date">Date</label>
          <input id="queue-date" type="date" className="form-input" value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)} />
        </div>
        <button type="button" className="btn btn-ghost" onClick={() => load(false)} disabled={refreshing}
          style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}>
          <Icon name="refresh" size={15} /> {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {status === 'loading' && (
        <div className="queue-board" aria-busy="true" aria-label="Loading the queue">
          {COLUMNS.map((c) => <div key={c.key} className="skeleton skeleton--hero" />)}
        </div>
      )}

      {status === 'error' && (
        <div className="alert alert-error" role="alert">
          {errorMsg}{' '}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => load(false)}>Try again</button>
        </div>
      )}

      {status === 'ready' && (
        <>
          <div className="queue-summary" aria-label="Queue summary">
            <span className="badge badge-confirmed">Avg wait: {stats.avgWaitMinutes == null ? '—' : `${stats.avgWaitMinutes} min`}</span>
            <span className={`badge ${stats.waitingOver20 ? 'badge-inactive' : 'badge-unknown'}`}>Waiting over 20 min: {stats.waitingOver20}</span>
            <span className="badge badge-pending">Waiting now: {stats.waitingCount}</span>
            <span className={`badge ${stats.noShows ? 'badge-inactive' : 'badge-unknown'}`}>No-shows: {stats.noShows}</span>
          </div>

          {entries.length === 0 ? (
            <div className="empty-state empty-state--panel">
              <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="calendar" size={20} /></div>
              <div className="empty-title">{isToday ? 'No patients expected today' : 'No patients on this date'}</div>
              <div className="empty-desc">Booked appointments appear here. Use Walk-in to add someone who just arrived.</div>
            </div>
          ) : (
            <div className="queue-board">
              {COLUMNS.map((c) => (
                <section key={c.key} className="queue-col" aria-labelledby={`col-${c.key}`}>
                  <div className="queue-col-head">
                    <span id={`col-${c.key}`}>{c.label}</span>
                    <span className="badge badge-unknown">{buckets[c.key].length}</span>
                  </div>
                  {buckets[c.key].length === 0
                    ? <div className="queue-col-empty">Nobody here</div>
                    : (
                      <ul className="queue-col-list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {buckets[c.key].map((e) => (
                          <QueueCard key={e.appointmentId} entry={e} now={now}
                            busy={busyId === e.appointmentId}
                            onCheckIn={handleCheckIn} onVitals={setVitalsFor} onStart={handleStart} />
                        ))}
                      </ul>
                    )}
                </section>
              ))}
            </div>
          )}
          {unknownCount > 0 && (
            <div className="alert alert-info" role="status" style={{ marginTop: 14 }}>
              {unknownCount} appointment{unknownCount === 1 ? ' has' : 's have'} a status this screen does not show yet.
            </div>
          )}
        </>
      )}

      {vitalsFor && (
        <VitalsIntakeForm
          patientId={vitalsFor.patientId}
          patientName={vitalsFor.patientName}
          appointmentId={vitalsFor.appointmentId}
          onClose={() => setVitalsFor(null)}
          onSaved={() => load(true)}
        />
      )}
      {pickingPatient && (
        <PatientSearchPicker
          title="Walk-in: find the patient"
          onClose={() => setPickingPatient(false)}
          onSelect={(p) => { setPickingPatient(false); setWalkInPatient(p); }}
        />
      )}
      {walkInPatient && (
        <WalkInDialog
          patient={walkInPatient}
          doctors={doctors}
          onClose={() => setWalkInPatient(null)}
          onCreated={() => { setWalkInPatient(null); load(true); }}
        />
      )}
    </AppShell>
  );
}
